import axiosInstance from "@/services/api";
import { AxiosError } from "axios";
import { useMediaAssetUpload } from "hooks/useMediaAssetUpload/";
import { VoiceProfileCreationResponse, VoiceProfileDetail } from "../types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCreate, useGo, useOne, useTranslate } from "@refinedev/core";
import { notification } from "antd";
import { ENABLE_MP3_ENCODING, VOICES_PROVIDERS } from "../constants";
import lamejs from "lamejs"; // Import lamejs
import { useAnalytics } from "hooks/useAnalytics/";
import { AnalyticsEvents } from "types/analytics/";
import { useCustomInvalidate } from "hooks/useCustomInvalidate/";
import { useOrganization } from "hooks/useOrganization/";
import { useSession } from "hooks/useSession";
import { useWsSubscription } from "hooks/useWsSubscription";
import { NOTIFICATIONS_EVENT_TYPE, WSEvent } from "store/websocketStore";
import { getQueryKey } from "../components/ContactWithPresenter/hooks/useInfiniteVoiceFaceProfiles";
import { ActiveTabName } from "../components/ContactWithPresenter/types";

export const DEFAULT_PROVIDER_ID = VOICES_PROVIDERS.ELEVEN_LABS;
export const DEFAULT_VOICE_NAME = "Voice";
export const MAX_UPLOADED_SAMPLE_DURATION = 120; // 2 minutes to avoid long audio files
export const MIN_TOTAL_DURATION = 30; // elevenlabs allow 30s (steve)
export const MAX_SAMPLE_DURATION = 35;
export const WAV_SAMPLE_RATE = 44100; // cf https://help.elevenlabs.io/hc/en-us/articles/15754340124305-What-audio-formats-do-you-support
export const RECORD_COUNTDOWN_DURATION = 3; // seconds
export const MAX_CLONING_WAIT_MS = 60 * 1000;

export interface RecordedSample {
  blob: Blob;
  audio: HTMLAudioElement;
}

export interface UploadedSample {
  file: File;
  audio: HTMLAudioElement;
}

export interface UseVoiceCloningOptions {
  defaultName?: string;
  language?: string;
  onCreated?: (voiceProfileId: string) => void;
  onSuccess?: (voiceProfileId: string) => void;
  onCreatingChange?: (isCreating: boolean) => void;
  autoCreateContact?: boolean;
}

export const loadSound = async (path: Blob): Promise<HTMLAudioElement> => {
  return new Promise((resolve) => {
    const sound = new Audio(URL.createObjectURL(path));
    sound.addEventListener("loadedmetadata", () => resolve(sound), false);
  });
};

export const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
};

export const useVoiceCloning = ({
  defaultName,
  language,
  onCreated,
  onSuccess,
  onCreatingChange,
  autoCreateContact = true,
}: UseVoiceCloningOptions) => {
  const { mutateAsync: mutateVoiceProfile } =
    useCreate<VoiceProfileCreationResponse>();
  const { organization } = useOrganization({});
  const t = useTranslate();
  const [isCreating, setIsCreating] = useState(false);
  const [voiceName, setVoiceName] = useState(DEFAULT_VOICE_NAME);
  const [recordedSamples, setRecordedSamples] = useState<RecordedSample[]>([]);
  const [uploadedSamples, setUploadedSamples] = useState<UploadedSample[]>([]);
  const [newVoiceProfile, setNewVoiceProfile] =
    useState<VoiceProfileCreationResponse>();
  const [consentChecked, setConsentChecked] = useState(false);
  const [isVoiceCloningComplete, setIsVoiceCloningComplete] = useState(false);
  const [isCloningTimedOut, setIsCloningTimedOut] = useState(false);

  const recordedSamplesRef = useRef<RecordedSample[]>([]);
  const uploadedSamplesRef = useRef<UploadedSample[]>([]);

  const { customInvalidate } = useCustomInvalidate();
  const go = useGo();

  const { user } = useSession();
  const hasInitializedVoiceName = useRef(false);

  // Subscribe to VoiceProfileStatusChanged for the freshly-created profile.
  // The BE routes this event with project_id = voice_profile_id (workaround,
  // see API doc for MED-6363), so we reuse the "project" SubscriptionType.
  const voiceProfileId = newVoiceProfile?.voice_profile_id;
  const wsSubscription = useMemo(
    () =>
      voiceProfileId
        ? { type: "project" as const, id: voiceProfileId }
        : { type: "project" as const, id: undefined },
    [voiceProfileId]
  );

  // Shared success path used by both the WS handler and the polling fallback.
  // Idempotent: once cloning is marked complete, subsequent calls no-op so the
  // navigation/onSuccess branch fires exactly once even if both paths resolve.
  const completeCloning = useCallback(() => {
    if (!voiceProfileId) return;
    setIsVoiceCloningComplete((alreadyComplete) => {
      if (alreadyComplete) return alreadyComplete;
      setIsCreating(false);
      if (organization?.id) {
        customInvalidate(
          getQueryKey("voice", ActiveTabName.Custom, organization.id)
        ).catch((error) => {
          console.error("[useVoiceCloning] error invalidating cache", error);
        });
      }
      if (autoCreateContact) {
        if (onSuccess) {
          onSuccess(voiceProfileId);
        } else {
          setTimeout(() => {
            go({ to: { resource: "media_voices", action: "list" } });
          }, 3000);
        }
      }
      return true;
    });
  }, [
    voiceProfileId,
    organization?.id,
    customInvalidate,
    autoCreateContact,
    onSuccess,
    go,
  ]);

  const handleVoiceProfileStatus = useCallback(
    (event: WSEvent) => {
      if (
        !voiceProfileId ||
        event.event !== NOTIFICATIONS_EVENT_TYPE.VoiceProfileStatusChanged ||
        event.operation !== "Cloning" ||
        event.project_id !== voiceProfileId
      ) {
        return;
      }
      if (event.status === "Success") {
        completeCloning();
      } else if (event.status === "Failed") {
        setIsCreating(false);
        notification.error({
          message: t("media.components.VoiceProfileCreateForm.cloningFailed"),
          description: t(
            "media.components.VoiceProfileCreateForm.cloningFailedDescription"
          ),
        });
      }
    },
    [voiceProfileId, completeCloning, t]
  );

  useWsSubscription(wsSubscription, handleVoiceProfileStatus);

  // Polling fallback: in some envs the WS reconnect loop swallows the Cloning
  // event, so we also poll the voice profile detail. Once the BE finishes
  // cloning it populates `original_language.preview_asset_path`; when we see
  // that, treat it as success. Stops automatically once cloning is complete.
  const voiceProfileDetailQuery = useOne<VoiceProfileDetail>({
    resource: `media/${organization?.id}/voice_profiles`,
    id: voiceProfileId,
    queryOptions: {
      enabled:
        !!voiceProfileId &&
        !!organization?.id &&
        !isVoiceCloningComplete &&
        !isCloningTimedOut,
      refetchInterval: (data) => {
        if (data?.data?.original_language?.preview_asset_path) {
          return false;
        }
        return 5000;
      },
    },
    errorNotification: false,
  });

  const polledPreviewPath =
    voiceProfileDetailQuery.data?.data?.original_language?.preview_asset_path;

  useEffect(() => {
    if (polledPreviewPath && !isVoiceCloningComplete) {
      completeCloning();
    }
  }, [polledPreviewPath, isVoiceCloningComplete, completeCloning]);

  // Hard cap on the cloning wait: if neither the WS event nor the polled
  // preview path resolves within MAX_CLONING_WAIT_MS, stop polling and release
  // the spinner so the user isn't stuck. The clone may still complete BE-side.
  useEffect(() => {
    if (!voiceProfileId || !isCreating) return;
    const timer = setTimeout(() => {
      setIsCloningTimedOut(true);
      setIsCreating(false);
      notification.error({
        message: t("media.components.VoiceProfileCreateForm.cloningFailed"),
        description: t(
          "media.components.VoiceProfileCreateForm.cloningFailedDescription"
        ),
      });
    }, MAX_CLONING_WAIT_MS);
    return () => clearTimeout(timer);
  }, [voiceProfileId, isCreating, t]);

  // Sync voice name once when defaultName or user becomes available.
  // This is user-editable state that needs initial sync from async-loaded props,
  // not purely derived state - disabling lint rule is intentional here.
  useEffect(() => {
    if (hasInitializedVoiceName.current) return;
    const newName = defaultName ?? user?.first_name;
    if (newName) {
      hasInitializedVoiceName.current = true;

      setVoiceName(newName);
    }
  }, [defaultName, user?.first_name]);

  const totalAudioSamplesDuration = [
    ...recordedSamples,
    ...uploadedSamples,
  ].reduce((total, sample) => total + Math.floor(sample.audio.duration), 0);
  const totalAudioSamples = recordedSamples.length + uploadedSamples.length;
  const canCreate =
    voiceName.length > 0 && totalAudioSamplesDuration >= MIN_TOTAL_DURATION;

  const { uploadFile, createFileFromBlob } = useMediaAssetUpload({
    organization: organization?.id,
    assetType: "Audio",
  });

  const { trackEvent } = useAnalytics();

  // Keep refs in sync with state for cleanup
  useEffect(() => {
    recordedSamplesRef.current = recordedSamples;
  }, [recordedSamples]);

  useEffect(() => {
    uploadedSamplesRef.current = uploadedSamples;
  }, [uploadedSamples]);

  // Cleanup object URLs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      recordedSamplesRef.current.forEach((sample) => {
        if (sample.audio.src) {
          URL.revokeObjectURL(sample.audio.src);
        }
      });
      uploadedSamplesRef.current.forEach((sample) => {
        if (sample.audio.src) {
          URL.revokeObjectURL(sample.audio.src);
        }
      });
    };
  }, []);

  // Notify parent when creating state changes
  useEffect(() => {
    onCreatingChange?.(isCreating);
  }, [isCreating, onCreatingChange]);

  const removeRecordedSample = (index: number) => {
    setRecordedSamples((prev) => {
      const sample = prev[index];
      if (sample?.audio.src) {
        URL.revokeObjectURL(sample.audio.src);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const removeUploadedSample = (index: number) => {
    setUploadedSamples((prev) => {
      const sample = prev[index];
      if (sample?.audio.src) {
        URL.revokeObjectURL(sample.audio.src);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const clearAllSamples = useCallback(() => {
    setRecordedSamples((prev) => {
      prev.forEach((sample) => {
        if (sample.audio.src) URL.revokeObjectURL(sample.audio.src);
      });
      return [];
    });
    setUploadedSamples((prev) => {
      prev.forEach((sample) => {
        if (sample.audio.src) URL.revokeObjectURL(sample.audio.src);
      });
      return [];
    });
  }, []);

  // Stable refs so callers can use these in useEffect dep arrays without
  // tearing down their effects on every parent re-render (e.g. while polling).
  const addRecordedSample = useCallback(async (blob: Blob) => {
    const audio = await loadSound(blob);
    setRecordedSamples((prev) => [...prev, { blob, audio }]);
  }, []);

  const addUploadedSample = useCallback(async (file: File) => {
    const audio = await loadSound(file);
    setUploadedSamples((prev) => [...prev, { file, audio }]);
  }, []);

  // https://github.com/zhuker/lamejs/issues/89
  const convertWavToMp3 = (wavBlob: Blob) => {
    return new Promise<Blob>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const wavBuffer = event.target?.result;
        console.debug({ wavBuffer });
        if (wavBuffer instanceof ArrayBuffer) {
          const wav = lamejs.WavHeader.readHeader(new DataView(wavBuffer));
          const recordedSamples = new Int16Array(
            wavBuffer,
            wav.dataOffset,
            wav.dataLen / 2
          );
          const mp3encoder = new lamejs.Mp3Encoder(1, WAV_SAMPLE_RATE, 128);
          const mp3Data: ArrayBuffer[] = [];
          const sampleBlockSize = 1152;
          for (let i = 0; i < recordedSamples.length; i += sampleBlockSize) {
            const sampleChunk = recordedSamples.subarray(
              i,
              i + sampleBlockSize
            );
            const mp3buf = mp3encoder.encodeBuffer(sampleChunk);
            if (mp3buf.length > 0) {
              mp3Data.push(mp3buf.buffer as ArrayBuffer);
            }
          }
          const mp3buf = mp3encoder.flush();
          if (mp3buf.length > 0) {
            mp3Data.push(mp3buf.buffer as ArrayBuffer);
          }
          const mp3Blob = new Blob(mp3Data, { type: "audio/mp3" });
          resolve(mp3Blob);
        } else {
          reject(
            new Error("Expected ArrayBuffer but received " + typeof wavBuffer)
          );
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(wavBlob);
    });
  };

  const createVoiceProfile = async () => {
    const createPayload = (file: File, newVoiceProfileId: string) =>
      [
        {},
        file,
        `/media/${organization?.id}/voice_profiles/${newVoiceProfileId}/assets`,
        {
          type: "CreateVoiceAsset",
          organization_id: organization?.id,
          category: "Voice",
          // Must be "upload" for the BE to auto-link the asset as a recording
          // for the cloning flow (verified against BE test scenario).
          source: "upload",
          description: "",
          asset: {
            type: "Speech",
            file_name: file.name,
          },
        },
      ] as const;
    try {
      setIsCreating(true);
      const newVoiceProfileResponse = await mutateVoiceProfile({
        resource: `media/${organization?.id}/voice_profiles`,
        values: {
          name: voiceName,
          provider_id: DEFAULT_PROVIDER_ID,
          settings: {},
          category: "Cloned",
          ...(language && {
            original_language: { language, accent: null, locale: null },
          }),
        },
        successNotification: {
          message: t("media.components.VoiceProfileCreateForm.yourVoiceIs"),
          type: "success",
          description: t("media.components.VoiceProfileCreateForm.success"),
        },
      });
      const createdVoiceProfile = newVoiceProfileResponse.data;
      const { voice_profile_id: newVoiceProfileId } = createdVoiceProfile;
      // Now we're ready to upload recordedSamples to it since that cannot be done
      // without first creating a VP entity.
      await Promise.all([
        ...recordedSamples.map(async (sample, index) => {
          const filename = ENABLE_MP3_ENCODING
            ? `audiosample_${index}.mp3`
            : `audiosample_${index}.wav`;
          const blob = ENABLE_MP3_ENCODING
            ? await convertWavToMp3(sample.blob)
            : sample.blob;
          const mimeType = ENABLE_MP3_ENCODING ? "audio/mp3" : "audio/wav";

          const file = createFileFromBlob(blob, filename, mimeType);
          // uploadFile creates the asset entity and uploads to S3.
          await uploadFile(...createPayload(file, newVoiceProfileId));
        }),
        ...uploadedSamples.map(async (sample) => {
          await uploadFile(...createPayload(sample.file, newVoiceProfileId));
        }),
      ]);
      setNewVoiceProfile(createdVoiceProfile);
      // Workaround: BE has eventual consistency between the S3 upload and the
      // asset becoming visible to the clone handler — clone returns 400
      // "Must attach at least one voice asset…" until the asset is observable.
      // Retry the clone POST on that specific message; any other failure is
      // surfaced immediately. Remove once BE awaits S3 commit before responding.
      const CLONE_RETRY_DELAY_MS = 800;
      const MAX_CLONE_RETRIES = 5;
      const ASSET_NOT_ATTACHED_MESSAGE = "Must attach at least one voice asset";
      const cloneUrl = `/media/${organization?.id}/voice_profiles/${newVoiceProfileId}/clone`;
      for (let attempt = 0; attempt <= MAX_CLONE_RETRIES; attempt++) {
        try {
          await axiosInstance.post(cloneUrl);
          break;
        } catch (error) {
          const axiosError = error as AxiosError<{ detail?: string }>;
          const detail = axiosError.response?.data.detail;
          const isAssetNotYetVisible =
            axiosError.response?.status === 400 &&
            typeof detail === "string" &&
            detail.includes(ASSET_NOT_ATTACHED_MESSAGE);
          if (!isAssetNotYetVisible || attempt === MAX_CLONE_RETRIES) {
            throw error;
          }
          await new Promise((resolve) =>
            setTimeout(resolve, CLONE_RETRY_DELAY_MS)
          );
        }
      }
      if (organization?.id) {
        await customInvalidate(
          getQueryKey("voice", ActiveTabName.Custom, organization.id)
        );
      }
      // Call onCreated immediately after voice profile is created (before cloning completes)
      onCreated?.(newVoiceProfileId);
      trackEvent(AnalyticsEvents.VoiceCloneCreate, {
        voice_id: newVoiceProfileId,
        samples_count: totalAudioSamples,
        samples_duration: totalAudioSamplesDuration,
      });
    } catch (error) {
      console.debug(error);
      notification.error({
        message: t("media.components.VoiceProfileCreateForm.cloningFailed"),
        description: t(
          "media.components.VoiceProfileCreateForm.cloningFailedDescription"
        ),
      });
      // Reset only on synchronous failure; on success, isCreating stays true
      // until the async cloning completes (WS event or polling).
      setIsCreating(false);
    }
  };

  return {
    isCreating,
    voiceName,
    setVoiceName,
    recordedSamples,
    setRecordedSamples,
    uploadedSamples,
    setUploadedSamples,
    newVoiceProfile,
    setNewVoiceProfile,
    consentChecked,
    setConsentChecked,
    createVoiceProfile,
    totalAudioSamplesDuration,
    totalAudioSamples,
    canCreate,
    removeRecordedSample,
    removeUploadedSample,
    clearAllSamples,
    addRecordedSample,
    addUploadedSample,
    isVoiceCloningComplete,
  };
};
